import Pusher from "pusher-js";
import API from "../api/api";

const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY || "823ab72ed6ecd563b8c8";
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || "ap2";

const config = () => {
  const token = localStorage.getItem("access_token");

  return {
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
};

const ZOOM_LINK_CHANNEL = "zoom-link";
const ZOOM_LINK_EVENT = "App\\Events\\ZoomLinkUpdate";

const getBroadcastAuthEndpoint = () => {
  const apiBase = process.env.REACT_APP_API_URL || "";
  const appBase = apiBase.replace(/\/api\/?$/, "").replace(/\/$/, "");

  return `${appBase}/broadcasting/auth`;
};

const normalizeApiResponse = (response) => {
  const body = response && response.data;

  if (!body) {
    throw new Error("Matching service is unavailable. Please try again.");
  }

  if (body.status === false) {
    throw new Error(body.message || "Matching service is unavailable. Please try again.");
  }

  return body.data || {};
};

class ImmediateOnDemandRealtime {
  constructor() {
    this.pusher = null;
    this.channel = null;
    this.channelName = null;
    // Separate connection for the public "zoom-link" channel so the matching
    // screen's disconnect() doesn't tear down the thank-you screen's listener.
    this.zoomPusher = null;
    this.zoomChannel = null;
    this.zoomHandlers = new Set();
  }

  getPusher() {
    if (!this.pusher) {
      const token = localStorage.getItem("access_token");

      Pusher.logToConsole = false;
      this.pusher = new Pusher(PUSHER_KEY, {
        cluster: PUSHER_CLUSTER,
        authEndpoint: getBroadcastAuthEndpoint(),
        auth: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      });
    }

    return this.pusher;
  }

  subscribe(userId, handlers = {}) {
    if (!userId) {
      return () => {};
    }

    const channelName = `private-immediate-on-demand.user.${userId}`;

    if (this.channel && this.channelName !== channelName) {
      this.unsubscribe();
    }

    if (!this.channel) {
      this.channelName = channelName;
      this.channel = this.getPusher().subscribe(channelName);
    }

    Object.entries(handlers).forEach(([eventName, handler]) => {
      this.channel.bind(eventName, handler);
    });

    return () => {
      if (!this.channel) return;

      Object.entries(handlers).forEach(([eventName, handler]) => {
        this.channel.unbind(eventName, handler);
      });
    };
  }

  unsubscribe() {
    if (this.pusher && this.channelName) {
      this.pusher.unsubscribe(this.channelName);
    }

    this.channel = null;
    this.channelName = null;
  }

  async joinProviderQueue(payload) {
    const response = await API.post("immediate-on-demand/provider/join", payload, config());
    return normalizeApiResponse(response);
  }

  async leaveProviderQueue(payload) {
    const response = await API.post("immediate-on-demand/provider/leave", payload, config());
    return normalizeApiResponse(response);
  }

  async heartbeatProviderQueue(payload) {
    const response = await API.post("immediate-on-demand/provider/heartbeat", payload, config());
    return normalizeApiResponse(response);
  }

  async requestClientMatch(payload) {
    const response = await API.post("immediate-on-demand/client/request-match", payload, config());
    return normalizeApiResponse(response);
  }

  async checkQueueAvailability(params) {
    const response = await API.get("immediate-on-demand/client/queue-availability", {
      ...config(),
      params,
    });
    return normalizeApiResponse(response);
  }

  async getEligibility(params) {
    const response = await API.get("immediate-on-demand/eligibility", {
      ...config(),
      params,
    });
    return normalizeApiResponse(response);
  }

  // Listens for App\\Events\\ZoomLinkUpdate (broadcast by the CreateZoomLink
  // job) and calls onLink(meetingLink) once the link for appointmentId arrives.
  subscribeZoomLink(appointmentId, onLink) {
    if (!appointmentId || typeof onLink !== "function") {
      return () => {};
    }

    const handler = (data) => {
      if (
        data &&
        data.meeting_link &&
        String(data.appointment_id) === String(appointmentId)
      ) {
        onLink(data.meeting_link);
      }
    };

    if (!this.zoomPusher) {
      this.zoomPusher = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
      this.zoomChannel = this.zoomPusher.subscribe(ZOOM_LINK_CHANNEL);
    }

    this.zoomChannel.bind(ZOOM_LINK_EVENT, handler);
    this.zoomHandlers.add(handler);

    return () => {
      if (this.zoomChannel) {
        this.zoomChannel.unbind(ZOOM_LINK_EVENT, handler);
      }
      this.zoomHandlers.delete(handler);

      if (!this.zoomHandlers.size && this.zoomPusher) {
        this.zoomPusher.unsubscribe(ZOOM_LINK_CHANNEL);
        this.zoomPusher.disconnect();
        this.zoomPusher = null;
        this.zoomChannel = null;
      }
    };
  }

  // Fallback for when the ZoomLinkUpdate event fired before we subscribed.
  // Resolves to the meeting link, or "" if it hasn't been generated yet.
  // Reads the appointment from get-appointment-zoom-link/{id}. Returns the
  // meeting link ("" until Zoom has generated it) and the Medium's email.
  async getAppointmentZoomDetails(appointmentId) {
    const response = await API.get(`get-appointment-zoom-link/${appointmentId}`, config());
    const data = (response && response.data && response.data.data) || {};
    const link = data.meeting_link;

    return {
      meetingLink: link && link !== "NULL" ? link : "",
      mediumEmail: data.medium_email || "",
    };
  }

  async getMeetingLink(appointmentId) {
    const details = await this.getAppointmentZoomDetails(appointmentId);
    return details.meetingLink;
  }


  disconnect() {
    this.unsubscribe();

    if (this.pusher) {
      this.pusher.disconnect();
    }

    this.pusher = null;
  }
}

const immediateOnDemandRealtime = new ImmediateOnDemandRealtime();

export default immediateOnDemandRealtime;
